/**
 * Client-side image compression before upload.
 *
 * Phone cameras routinely shoot 4-8 MB JPEG or 3-5 MB HEIC. Those
 * files hurt uploads, R2 costs, and server-side Edge Function calls
 * that base64 the image for Claude vision (which hard-caps inputs at
 * 5 MB). This helper always re-encodes via canvas with an adaptive
 * quality ladder so output is guaranteed under TARGET_BYTES.
 *
 * Bonuses:
 *   * EXIF rotation is baked in (createImageBitmap applies orientation)
 *   * Strips EXIF, GPS, camera metadata on re-encode (PII win for bidders)
 *   * HEIC/HEIF from iOS decode via createImageBitmap in Safari; fails
 *     silently in Chrome/Firefox — in that case we return the original
 *     File untouched (better than blocking the upload).
 */

const MAX_DIM = 2048;
const FALLBACK_DIM = 1600;
const TARGET_BYTES = 4 * 1024 * 1024; // leave headroom below Anthropic's 5 MB cap
const QUALITY_LADDER = [0.85, 0.72, 0.6, 0.5, 0.4];
const SKIP_UNDER_BYTES = 200 * 1024;

async function drawToBlob(
  bitmap: ImageBitmap,
  maxDim: number,
  quality: number,
): Promise<Blob | null> {
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });
}

export async function compressImage(file: File): Promise<File> {
  // GIFs would flatten to the first frame on canvas — skip.
  if (file.type === 'image/gif') return file;

  // Genuinely small files aren't worth the round trip.
  if (file.size <= SKIP_UNDER_BYTES) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // HEIC on Chrome/Firefox, corrupt file, etc. Fall back to the original.
    return file;
  }

  try {
    // First pass at the best dimension + quality we'd like. Then walk the
    // quality ladder if we're still over target. If quality alone can't
    // bring it in, drop to FALLBACK_DIM and try again.
    for (const dim of [MAX_DIM, FALLBACK_DIM]) {
      for (const quality of QUALITY_LADDER) {
        const blob = await drawToBlob(bitmap, dim, quality);
        if (!blob) continue;
        if (blob.size <= TARGET_BYTES) {
          const baseName = file.name.replace(/\.[^./\\]+$/, '');
          return new File([blob], `${baseName}.jpg`, {
            type: 'image/jpeg',
            lastModified: file.lastModified,
          });
        }
      }
    }
    // Worst case: return the final attempt even if it's still over target —
    // it'll be tiny at 40 % / 1600 px, ~1-2 MB at most for any reasonable input.
    const finalBlob = await drawToBlob(bitmap, FALLBACK_DIM, 0.4);
    if (finalBlob) {
      const baseName = file.name.replace(/\.[^./\\]+$/, '');
      return new File([finalBlob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    }
    return file;
  } finally {
    bitmap.close?.();
  }
}
